// ResourcesAdminTab.jsx — Admin Panel "Resources" sub-tab, extracted from
// LimitedPartnerPortal. Resource CRUD form, filterable list, and the CSV
// template download / bulk-import preview flow. loadManagedResources and the
// list/form/filter state stay lifted in the portal — a parent effect reloads
// the list when this sub-tab is opened, and drafts survive sub-tab switches.
import React from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../../firebaseConfig.js";
import { saveAs } from "file-saver";
import { RetroButton, EmptyState } from "../../../ui/retro";
import { buildResourceCsvTemplate, parseResourceCsv } from "../../resources/resourceCsv";

// Seven-column grid shared by the list header and every resource row.
const RESOURCES_GRID = "1.7fr 1fr 1fr 0.7fr 1.2fr 1fr 132px";

export default function ResourcesAdminTab({
  user,
  userChapter,
  isChapterDirector,
  activeChapterNames,
  managedResources,
  editingResource,
  setEditingResource,
  resourceFormData,
  setResourceFormData,
  isAddingResource,
  setIsAddingResource,
  resourceSearchTerm,
  setResourceSearchTerm,
  resourceTypeFilter,
  setResourceTypeFilter,
  resourceStageFilter,
  setResourceStageFilter,
  resourceChapterFilter,
  setResourceChapterFilter,
  resourceCsvImport,
  setResourceCsvImport,
  resourceCsvFileInputRef,
  loadManagedResources,
  requestConfirm,
  showAppAlert,
}) {
const handleSaveResource = async () => {
  try {
    // Validate required fields
    if (!resourceFormData.Resource || !resourceFormData.Chapter || !resourceFormData.Type || !resourceFormData['Business Stage']) {
      showAppAlert("Please fill in all required fields: Resource Name, Chapter, Type, and Business Stage.");
      return;
    }

    // Map form data to Firestore field names
    const firestoreData = {
      Resource: resourceFormData.Resource,
      Chapter: resourceFormData.Chapter,
      Type: resourceFormData.Type,
      Stage: resourceFormData['Business Stage'],
      FocusArea: resourceFormData['Focus Area'] || '',
      CountiesServed: resourceFormData['Counties Served'] || '',
      Website: resourceFormData.URL || '',
      About: resourceFormData['Expanded Details'] || '',
      AverageCheckSize: resourceFormData['Average Check Size'] || '',
      RelocationRequired: resourceFormData['Relocation Required?'] || 'No'
    };
    
    if (editingResource) {
      // Update existing resource
      const resourceRef = doc(db, 'resources', editingResource.id);
      await updateDoc(resourceRef, {
        ...firestoreData,
        updatedAt: Timestamp.now()
      });
      showAppAlert("Resource updated successfully!");
    } else {
      // Add new resource
      const resourcesRef = collection(db, 'resources');
      await addDoc(resourcesRef, {
        ...firestoreData,
        createdAt: Timestamp.now(),
        createdBy: user.uid
      });
      showAppAlert("Resource added successfully!");
    }
    
    // Reset form and reload
    setEditingResource(null);
    setIsAddingResource(false);
    setResourceFormData({
      Resource: '',
      Chapter: '',
      Type: '',
      'Focus Area': '',
      'Business Stage': 'Ideation',
      'Counties Served': '',
      URL: '',
      'Expanded Details': '',
      'Average Check Size': '',
      'Relocation Required?': 'No'
    });
    loadManagedResources();
  } catch (error) {
    console.error("LPPortal: Error saving resource:", error);
    showAppAlert("Failed to save resource.");
  }
};

const handleDeleteResource = async (resourceId) => {
  const ok = await requestConfirm({
    title: 'Delete this resource?',
    message: 'This cannot be undone. The resource will disappear from every chapter page immediately.',
    confirmLabel: 'Delete',
    variant: 'danger',
  });
  if (!ok) return;
  try {
    await deleteDoc(doc(db, "resources", resourceId));
    showAppAlert("Resource deleted successfully!");
    loadManagedResources();
  } catch (error) {
    console.error("LPPortal: Error deleting resource:", error);
    showAppAlert("Failed to delete resource.");
  }
};

const handleEditResource = (resource) => {
  setEditingResource(resource);
  setResourceFormData({
    Resource: resource.Resource || resource.resource || '',
    Chapter: resource.Chapter || resource.chapter || '',
    Type: resource.Type || resource.type || '',
    'Focus Area': resource.FocusArea || resource['Focus Area'] || resource.focusArea || '',
    'Business Stage': resource.Stage || resource['Business Stage'] || resource.businessStage || 'Ideation',
    'Counties Served': resource.CountiesServed || resource['Counties Served'] || resource.countiesServed || '',
    URL: resource.Website || resource.URL || resource.url || '',
    'Expanded Details': resource.About || resource['Expanded Details'] || resource.expandedDetails || '',
    'Average Check Size': resource.AverageCheckSize || resource['Average Check Size'] || resource.averageCheckSize || '',
    'Relocation Required?': resource.RelocationRequired || resource['Relocation Required?'] || resource.relocationRequired || 'No'
  });
  setIsAddingResource(true);
};

// Download a CSV template scoped to the current user's chapter (when
// they're a chapter director) so the example rows already point at
// the right place. Super admins get the default chapter in examples.
const handleDownloadResourceTemplate = () => {
  const chapterForExamples = isChapterDirector && userChapter ? userChapter : '';
  const csv = buildResourceCsvTemplate(chapterForExamples);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const filename = chapterForExamples
    ? `resources-template-${chapterForExamples.toLowerCase().replace(/\s+/g, '-')}.csv`
    : 'resources-template.csv';
  saveAs(blob, filename);
};

const handleResourceCsvFile = (e) => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (evt) => {
    const text = evt.target.result;
    const allowedChapters = isChapterDirector && userChapter ? [userChapter] : activeChapterNames;
    const result = parseResourceCsv(text, {
      forcedChapter: isChapterDirector ? userChapter : null,
      allowedChapters,
    });
    setResourceCsvImport({ ...result, status: 'preview' });
  };
  reader.readAsText(file);
  // Reset the input so re-uploading the same file fires onChange.
  e.target.value = '';
};

const handleCancelResourceImport = () => {
  setResourceCsvImport({ valid: [], errors: [], status: 'idle' });
};

const handleCommitResourceImport = async () => {
  if (!resourceCsvImport.valid.length) return;
  setResourceCsvImport((prev) => ({ ...prev, status: 'committing' }));
  try {
    // Firestore writeBatch caps at 500 ops. We chunk so a 600-row CSV
    // doesn't silently drop rows past the cap.
    const chunks = [];
    for (let i = 0; i < resourceCsvImport.valid.length; i += 400) {
      chunks.push(resourceCsvImport.valid.slice(i, i + 400));
    }
    for (const chunk of chunks) {
      const batch = writeBatch(db);
      const resourcesRef = collection(db, 'resources');
      chunk.forEach((row) => {
        const ref = doc(resourcesRef);
        batch.set(ref, {
          ...row,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
    }
    showAppAlert(`Imported ${resourceCsvImport.valid.length} resources.`);
    setResourceCsvImport({ valid: [], errors: [], status: 'idle' });
    loadManagedResources();
  } catch (error) {
    console.error('LPPortal: Bulk resource import failed:', error);
    showAppAlert('Bulk import failed. Some rows may not have been saved.');
    setResourceCsvImport((prev) => ({ ...prev, status: 'preview' }));
  }
};

            const RESOURCE_TYPES = [
              'Funding', 'Incubator/Accelerator', 'Mentorship', 'Community', 'Government',
              'Legal', 'Education', 'Venture Capital', 'Angel Group', 'Private Investment Office',
              'Corporate Venture', 'Venture Studio', 'Coworking', 'Nonprofit', 'Private Equity',
              'Investment Platform'
            ];
            const BUSINESS_STAGES = ['Ideation', 'Early', 'Growth', 'Established', 'All'];
            // Chapter directors see only their own chapter's resources. The
            // Firestore rule enforces the same boundary on writes; this is the
            // UI-side complement so the rest of the tab (filters, table, stats)
            // operates against an already-scoped list.
            const scopedResources = isChapterDirector && userChapter
              ? managedResources.filter(r => (r.Chapter || r.chapter) === userChapter)
              : managedResources;
            const filteredResources = scopedResources.filter(resource => {
              const search = resourceSearchTerm.toLowerCase();
              const matchesSearch = !resourceSearchTerm ||
                resource.Resource?.toLowerCase().includes(search) ||
                resource.Type?.toLowerCase().includes(search) ||
                resource.FocusArea?.toLowerCase().includes(search) ||
                resource['Focus Area']?.toLowerCase().includes(search) ||
                (resource.Chapter || resource.chapter || '').toLowerCase().includes(search);
              const matchesType = !resourceTypeFilter ||
                resource.Type === resourceTypeFilter || resource.type === resourceTypeFilter;
              const matchesStage = !resourceStageFilter ||
                resource.Stage === resourceStageFilter ||
                resource['Business Stage'] === resourceStageFilter ||
                resource.businessStage === resourceStageFilter;
              const resourceChapter = resource.Chapter || resource.chapter || '';
              const matchesChapter = !resourceChapterFilter || resourceChapter === resourceChapterFilter;
              return matchesSearch && matchesType && matchesStage && matchesChapter;
            });
            const hasFilters = resourceSearchTerm || resourceTypeFilter || resourceStageFilter || resourceChapterFilter;
            // Local style stubs — empty so .mb-form-shell + .filter-bar can take over.
            const labelStyle = {};
            const inputStyle = { width: '100%' };
            const clearFilters = () => {
              setResourceSearchTerm('');
              setResourceTypeFilter('');
              setResourceStageFilter('');
              setResourceChapterFilter('');
            };
            const openAddForm = () => {
              setEditingResource(null);
              setResourceFormData({
                Resource: '',
                // Pin chapter to the director's own chapter — they cannot
                // create resources for other chapters (Firestore enforces it).
                Chapter: isChapterDirector && userChapter ? userChapter : '',
                Type: '', 'Focus Area': '',
                'Business Stage': 'Ideation', 'Counties Served': '', URL: '',
                'Expanded Details': '', 'Average Check Size': '', 'Relocation Required?': 'No'
              });
              setIsAddingResource(true);
            };

            const uniqueChapters = new Set(scopedResources.map(r => r.Chapter || r.chapter).filter(Boolean)).size;
            return (
            <>
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    {isChapterDirector && userChapter ? userChapter : "All chapters"} · founder resource index
                  </div>
                  <h2 className="win95-pane-header__title">Resources</h2>
                </div>
                <div className="win95-pane-header__stats">
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Resources</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-magenta-deep)" }}>
                      {scopedResources.length}
                    </div>
                  </div>
                  {!isChapterDirector && (
                    <div className="win95-statpanel">
                      <div className="win95-statpanel__label">Chapters indexed</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-aqua-deep)" }}>
                        {uniqueChapters}
                      </div>
                    </div>
                  )}
                  {hasFilters && (
                    <div className="win95-statpanel">
                      <div className="win95-statpanel__label">Shown</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-tangerine-deep)" }}>
                        {filteredResources.length}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Command toolbar — template download, CSV import, add */}
              <div className="win95-toolbar" role="group" aria-label="Resource commands">
                <button type="button" className="win95-btn win95-btn--compact" onClick={handleDownloadResourceTemplate}>
                  Download template
                </button>
                <button
                  type="button"
                  className="win95-btn win95-btn--compact"
                  onClick={() => resourceCsvFileInputRef.current && resourceCsvFileInputRef.current.click()}
                >
                  Upload CSV
                </button>
                <input
                  ref={resourceCsvFileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleResourceCsvFile}
                  style={{ display: 'none' }}
                />
                <div className="win95-toolbar__spacer" />
                <button type="button" className="win95-btn win95-btn-primary win95-btn--compact" onClick={openAddForm}>
                  + Add resource
                </button>
              </div>

              <div className="admin-pane-body">
              {/* Toolbar: search + filters */}
              <div className="filter-bar filter-bar--one-row">
                <input
                  type="search"
                  aria-label="Search resources"
                  placeholder="Search name, type, focus, chapter…"
                  value={resourceSearchTerm}
                  onChange={(e) => setResourceSearchTerm(e.target.value)}
                />
                {!isChapterDirector && (
                  <select
                    aria-label="Filter by chapter"
                    value={resourceChapterFilter}
                    onChange={(e) => setResourceChapterFilter(e.target.value)}
                  >
                    <option value="">All chapters</option>
                    {activeChapterNames.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                <select
                  aria-label="Filter by type"
                  value={resourceTypeFilter}
                  onChange={(e) => setResourceTypeFilter(e.target.value)}
                >
                  <option value="">All types</option>
                  {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select
                  aria-label="Filter by stage"
                  value={resourceStageFilter}
                  onChange={(e) => setResourceStageFilter(e.target.value)}
                >
                  <option value="">All stages</option>
                  {BUSINESS_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <RetroButton onClick={clearFilters} disabled={!hasFilters}>
                  Clear
                </RetroButton>
              </div>

              {/* CSV import preview — appears after a parse, before commit. */}
              {resourceCsvImport.status !== 'idle' && (
                <div className="mb-form-shell" style={{
                  background: 'var(--chrome-face)',
                  border: '2px solid',
                  borderColor: 'var(--bevel-raised)',
                  boxShadow: 'var(--shadow-raised)',
                  padding: '14px 16px',
                  marginBottom: '20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                    <h5 style={{ margin: 0 }}>
                      Bulk import preview
                    </h5>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <RetroButton onClick={handleCancelResourceImport} disabled={resourceCsvImport.status === 'committing'}>
                        Cancel
                      </RetroButton>
                      <RetroButton
                        variant="primary"
                        onClick={handleCommitResourceImport}
                        disabled={!resourceCsvImport.valid.length || resourceCsvImport.status === 'committing'}
                      >
                        {resourceCsvImport.status === 'committing'
                          ? 'Importing…'
                          : `Import ${resourceCsvImport.valid.length} resource${resourceCsvImport.valid.length === 1 ? '' : 's'}`}
                      </RetroButton>
                    </div>
                  </div>

                  <p style={{ margin: '0 0 14px 0', fontSize: 13, color: 'var(--mb-ink-60)' }}>
                    {resourceCsvImport.valid.length} valid · {resourceCsvImport.errors.length} with issues
                    {isChapterDirector && userChapter ? ` · all rows pinned to ${userChapter}.` : '.'}
                    {' '}Review below, then click Import to write to Firestore.
                  </p>

                  {resourceCsvImport.errors.length > 0 && (
                    <div style={{
                      background: 'var(--mb-paper)',
                      border: '2px solid var(--mb-magenta)',
                      padding: 12,
                      marginBottom: 14,
                      maxHeight: 200,
                      overflowY: 'auto',
                    }}>
                      <strong style={{ color: 'var(--mb-magenta)', fontSize: 13 }}>
                        {resourceCsvImport.errors.length} row{resourceCsvImport.errors.length === 1 ? '' : 's'} skipped:
                      </strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 12, color: 'var(--mb-ink)' }}>
                        {resourceCsvImport.errors.map((err, i) => (
                          <li key={i}>
                            <strong>Row {err.rowIndex}{err.name ? ` — ${err.name}` : ''}:</strong>{' '}
                            {Array.isArray(err.messages) ? err.messages.join('; ') : err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {resourceCsvImport.valid.length > 0 && (
                    <div style={{
                      maxHeight: 260,
                      overflowY: 'auto',
                      border: '1px solid var(--mb-ink-15)',
                    }}>
                      <table className="retro-table" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Resource</th>
                            <th>Type</th>
                            <th>Stage</th>
                            <th>Chapter</th>
                            <th>Focus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resourceCsvImport.valid.map((row, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 700 }}>{row.Resource}</td>
                              <td>{row.Type}</td>
                              <td>{row['Business Stage']}</td>
                              <td>{row.Chapter}</td>
                              <td style={{ fontSize: 12, color: 'var(--mb-ink-60)' }}>
                                {row['Focus Area'] || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Resource Form (Add/Edit) */}
              {isAddingResource && (
                <div className="mb-form-shell" style={{
                  background: 'var(--chrome-face)',
                  border: '2px solid',
                  borderColor: 'var(--bevel-raised)',
                  boxShadow: 'var(--shadow-raised)',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h5 style={{ margin: '0 0 20px 0' }}>
                    {editingResource ? 'Edit resource' : 'Add new resource'}
                  </h5>

                  {/* Section 1: Basics */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      fontFamily: 'var(--font-chrome)',
                      fontSize: '11px', fontWeight: 600, letterSpacing: ' 0.01em',
                      textTransform: 'none', color: 'var(--chrome-text-soft)',
                      paddingBottom: '6px', marginBottom: '14px',
                      borderBottom: '1px solid var(--chrome-shade)'
                    }}>
                      Basics
                    </div>
                    <div className="admin-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>Resource name *</label>
                        <input
                          type="text"
                          value={resourceFormData.Resource}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, Resource: e.target.value })}
                          style={inputStyle}
                          placeholder="e.g., 43North Accelerator"
                          maxLength={80}
                        />
                        <small style={{ display: 'block', fontSize: 11, color: 'var(--mb-ink-60)', marginTop: 4 }}>
                          2-80 characters. Org or program name as a founder would search for it.
                        </small>
                      </div>
                      <div>
                        <label style={labelStyle}>Chapter *</label>
                        <select
                          value={resourceFormData.Chapter}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, Chapter: e.target.value })}
                          // Chapter directors cannot move a resource between
                          // chapters — Firestore would reject the write anyway.
                          disabled={isChapterDirector}
                          style={{
                            ...inputStyle,
                            background: resourceFormData.Chapter ? 'var(--gnf-bg)' : 'var(--gnf-yellow-100)'
                          }}
                        >
                          <option value="">— Select chapter —</option>
                          {activeChapterNames.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="admin-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div>
                        <label style={labelStyle}>Type *</label>
                        <select
                          value={resourceFormData.Type}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, Type: e.target.value })}
                          style={inputStyle}
                        >
                          <option value="">Select type</option>
                          {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Business stage *</label>
                        <select
                          value={resourceFormData['Business Stage']}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, 'Business Stage': e.target.value })}
                          style={inputStyle}
                        >
                          {BUSINESS_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Focus & Reach */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      fontFamily: 'var(--font-chrome)',
                      fontSize: '11px', fontWeight: 600, letterSpacing: ' 0.01em',
                      textTransform: 'none', color: 'var(--chrome-text-soft)',
                      paddingBottom: '6px', marginBottom: '14px',
                      borderBottom: '1px solid var(--chrome-shade)'
                    }}>
                      Focus &amp; reach
                    </div>
                    <div className="admin-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>Focus area</label>
                        <input
                          type="text"
                          value={resourceFormData['Focus Area']}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, 'Focus Area': e.target.value })}
                          style={inputStyle}
                          placeholder="e.g., High-growth startups"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Counties served</label>
                        <input
                          type="text"
                          value={resourceFormData['Counties Served']}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, 'Counties Served': e.target.value })}
                          style={inputStyle}
                          placeholder="e.g., All 8 counties, Erie, Niagara"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Average check size</label>
                        <input
                          type="text"
                          value={resourceFormData['Average Check Size']}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, 'Average Check Size': e.target.value })}
                          style={inputStyle}
                          placeholder="e.g., $50K-$250K"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Relocation required?</label>
                        <select
                          value={resourceFormData['Relocation Required?']}
                          onChange={(e) => setResourceFormData({ ...resourceFormData, 'Relocation Required?': e.target.value })}
                          style={inputStyle}
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Details */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      fontFamily: 'var(--font-chrome)',
                      fontSize: '11px', fontWeight: 600, letterSpacing: ' 0.01em',
                      textTransform: 'none', color: 'var(--chrome-text-soft)',
                      paddingBottom: '6px', marginBottom: '14px',
                      borderBottom: '1px solid var(--chrome-shade)'
                    }}>
                      Details
                    </div>
                    <div>
                      <label style={labelStyle}>URL</label>
                      <input
                        type="url"
                        value={resourceFormData.URL}
                        onChange={(e) => setResourceFormData({ ...resourceFormData, URL: e.target.value })}
                        style={inputStyle}
                        placeholder="https://..."
                      />
                      <small style={{ display: 'block', fontSize: 11, color: 'var(--mb-ink-60)', marginTop: 4 }}>
                        Full URL with the https:// prefix.
                      </small>
                    </div>
                    {(() => {
                      const detailLen = (resourceFormData['Expanded Details'] || '').length;
                      const inSweetSpot = detailLen >= 200 && detailLen <= 500;
                      const overCap = detailLen > 800;
                      const counterColor = overCap
                        ? 'var(--mb-magenta)'
                        : inSweetSpot
                        ? 'var(--mb-aqua)'
                        : 'var(--mb-ink-60)';
                      return (
                        <div style={{ marginTop: '12px' }}>
                          <label style={labelStyle}>Expanded details</label>
                          <textarea
                            value={resourceFormData['Expanded Details']}
                            onChange={(e) => setResourceFormData({ ...resourceFormData, 'Expanded Details': e.target.value })}
                            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                            placeholder="What does the founder actually get? Who is this for? E.g., 'Annual competition awarding $1M each to 5 startups. Companies relocate to Buffalo for 12 months and receive workspace, mentorship, and investor access. Best fit for traction-stage tech founders.'"
                            maxLength={800}
                          />
                          <small style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 12,
                            fontSize: 11,
                            color: 'var(--mb-ink-60)',
                            marginTop: 4,
                          }}>
                            <span>
                              1-3 sentences. ~200-500 chars hits the sweet spot. Avoid marketing language ("innovative", "cutting-edge").
                            </span>
                            <span style={{ color: counterColor, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {detailLen} / 800
                            </span>
                          </small>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="retro-action-row">
                    <RetroButton onClick={handleSaveResource} variant="primary">
                      {editingResource ? 'Update resource' : 'Save resource'}
                    </RetroButton>
                    <RetroButton
                      onClick={() => {
                        setIsAddingResource(false);
                        setEditingResource(null);
                      }}
                    >
                      Cancel
                    </RetroButton>
                  </div>
                </div>
              )}

              {/* Resources list — dense chrome rows */}
              <div className="admin-list-scroll">
                <div className="win95-listhead" style={{ gridTemplateColumns: RESOURCES_GRID }}>
                  {["Resource", "Chapter", "Type", "Stage", "Focus area", "Counties", "Actions"].map((h) => (
                    <div key={h} className="win95-listhead__cell">{h}</div>
                  ))}
                </div>
                <div className="win95-listbody" role="list" aria-label="Resources">
                  {filteredResources.map((resource) => {
                    const chapter = resource.Chapter || resource.chapter || '';
                    const url = resource.Website || resource.URL || resource.url;
                    return (
                      <div
                        key={resource.id}
                        role="listitem"
                        className="win95-row"
                        style={{ gridTemplateColumns: RESOURCES_GRID }}
                      >
                        <div className="win95-row__cell win95-row__cell--primary">
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {resource.Resource || resource.resource || '—'}
                          </span>
                          {url && (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: 11, fontWeight: 500, flex: 'none' }}
                              title={url}
                              aria-label={`Open ${resource.Resource || resource.resource || 'resource'} in new tab`}
                            >
                              Visit ↗
                            </a>
                          )}
                        </div>
                        <div className="win95-row__cell win95-row__cell--meta">
                          {chapter ? (
                            <span className="win95-chip" style={{ background: 'var(--mb-magenta)', color: 'var(--mb-chalk)' }}>
                              {chapter}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--chrome-text-soft)', fontStyle: 'italic' }}>Unassigned</span>
                          )}
                        </div>
                        <div className="win95-row__cell win95-row__cell--meta">{resource.Type || resource.type || '—'}</div>
                        <div className="win95-row__cell win95-row__cell--meta">{resource.Stage || resource['Business Stage'] || resource.businessStage || '—'}</div>
                        <div className="win95-row__cell win95-row__cell--meta" title={resource.FocusArea || resource['Focus Area'] || resource.focusArea || undefined}>
                          {resource.FocusArea || resource['Focus Area'] || resource.focusArea || '—'}
                        </div>
                        <div className="win95-row__cell win95-row__cell--meta" title={resource.CountiesServed || resource['Counties Served'] || resource.countiesServed || undefined}>
                          {resource.CountiesServed || resource['Counties Served'] || resource.countiesServed || '—'}
                        </div>
                        <div className="win95-row__cell win95-row__cell--actions" style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className="win95-btn win95-btn--compact"
                            onClick={() => handleEditResource(resource)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="win95-btn win95-btn-danger win95-btn--compact"
                            onClick={() => handleDeleteResource(resource.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {scopedResources.length === 0 && (
                  <EmptyState
                    icon={null}
                    title="No resources yet"
                    description="Click + Add Resource to create the first one."
                  />
                )}
                {scopedResources.length > 0 && filteredResources.length === 0 && (
                  <EmptyState
                    icon={null}
                    title="No resources match"
                    description="Try a different filter."
                    action={
                      <RetroButton size="sm" onClick={() => clearFilters()}>
                        Clear filters
                      </RetroButton>
                    }
                  />
                )}
              </div>
              </div>
            </>
            );
}
